import { useEffect, useMemo, useState } from "react";
import { BatteryMedium, Link2, LockKeyhole, Radio, Snowflake, Thermometer, X } from "lucide-react";
import { listTrailers } from "../api";
import type { Trailer } from "../types";

export function TrailerPanel({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [trailers, setTrailers] = useState<Trailer[]>([]);
  const [filter, setFilter] = useState<"all" | "reefer" | "seal">("all");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    const refresh = () => {
      setError("");
      listTrailers()
        .then(setTrailers)
        .catch((reason) => setError(reason instanceof Error ? reason.message : "No fue posible cargar remolques"));
    };
    refresh();
    window.addEventListener("gsf:trailer-updated", refresh);
    return () => window.removeEventListener("gsf:trailer-updated", refresh);
  }, [open]);

  const visible = useMemo(
    () => trailers.filter((trailer) => {
      if (filter === "reefer") return trailer.trailer_type === "reefer";
      if (filter === "seal") return trailer.seal_status !== "sealed";
      return true;
    }),
    [filter, trailers],
  );

  if (!open) return null;
  return (
    <div className="admin-overlay" role="presentation" onMouseDown={onClose}>
      <aside
        className="admin-panel trailer-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="trailer-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header>
          <div>
            <span className="eyebrow">REMOLQUES · TRACKER AUTÓNOMO</span>
            <h2 id="trailer-title">Carga y emparejamiento</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar remolques"><X size={18} /></button>
        </header>
        <section className="admin-panel__summary">
          <span><Link2 size={17} /><b>{trailers.filter((trailer) => trailer.assigned_vehicle_id).length}</b> emparejados</span>
          <span><Snowflake size={17} /><b>{trailers.filter((trailer) => trailer.trailer_type === "reefer").length}</b> reefers</span>
          <span><LockKeyhole size={17} /><b>{trailers.filter((trailer) => trailer.seal_status !== "sealed").length}</b> sellos abiertos</span>
        </section>
        <div className="trailer-panel__body">
          <div className="trailer-filters">
            {(["all", "reefer", "seal"] as const).map((value) => (
              <button className={filter === value ? "is-active" : ""} onClick={() => setFilter(value)} key={value}>
                {value === "all" ? "TODOS" : value === "reefer" ? "CADENA FRÍA" : "SELLO ABIERTO"}
              </button>
            ))}
            <b>{visible.length} ACTIVOS</b>
          </div>
          <div className="trailer-grid">
            {visible.map((trailer) => (
              <article className={`trailer-card trailer-card--${trailer.seal_status}`} key={trailer.id}>
                <header>
                  <span className={`status-dot is-${trailer.status}`} />
                  <strong>{trailer.unit_code}</strong>
                  <b>{trailer.trailer_type.replace("_", " ").toUpperCase()}</b>
                </header>
                <div className="trailer-card__shape">
                  <span>{trailer.trailer_type === "reefer" && <Snowflake size={22} />}</span>
                  <i /><i />
                </div>
                <dl>
                  <div><dt>TRACTO</dt><dd>{trailer.assigned_unit_code || "SIN ASIGNAR"}</dd></div>
                  <div><dt>CARGA</dt><dd>{trailer.cargo_description || "Sin manifiesto"}</dd></div>
                  <div><dt>SELLO</dt><dd className={trailer.seal_status !== "sealed" ? "is-alert" : ""}>{trailer.seal_status} · {trailer.seal_code}</dd></div>
                </dl>
                <footer>
                  <span><Radio size={11} /> {trailer.tracker_serial}</span>
                  <span><BatteryMedium size={11} /> {trailer.tracker_battery_percent ?? "—"}%</span>
                  {trailer.temperature_c !== null && <span><Thermometer size={11} /> {trailer.temperature_c}°C</span>}
                </footer>
              </article>
            ))}
          </div>
        </div>
        {error && <div className="admin-message admin-message--error">{error}</div>}
      </aside>
    </div>
  );
}
